/**
 * ContentDocView (Issue #2212)
 *
 * Renders a static ContentDoc (About, FAQ, Privacy, Terms, SMS Help)
 * through the standard page components (PageHeader, Section, CalloutCard,
 * Divider, LastUpdated). The native app renders the same data through its
 * own screen — content is single-sourced in `frontend/src/data/content/`.
 *
 * Link segments render as react-router <Link> anchors, except
 * `webRender: 'button'` which renders a navigate() button (the Terms page
 * Privacy Policy links are pinned as buttons by TermsPage.test.tsx).
 *
 * #3324: when this doc renders inside a dialog (the login legal modal),
 * navigating away would tear the host surface down. Pass `onNavigate` and
 * BOTH the `webRender: 'button'` inlines and the in-app `<Link>` inlines
 * become buttons that call it instead of routing — the host decides whether
 * to switch the doc in place (terms <-> privacy) or close and route. External
 * hrefs (mailto:/tel:/http) are untouched: they never disturb the host
 * surface. With no `onNavigate` (the standalone pages) behaviour is unchanged.
 */

import { Fragment, createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ContentDocBlock,
  ContentDoc,
  ContentInline,
  ContentDocSection,
} from '../types/content';
import OroTrademark from './OroTrademark';
import PageHeader from './PageHeader';
import Section from './Section';
import SectionGroup from './SectionGroup';
import CalloutCard from './CalloutCard';
import Divider from './Divider';
import LastUpdated from './LastUpdated';

const LINK_BASE_CLASS = 'underline transition-colors text-accent hover:text-accent/80';
const linkClass = (bold?: boolean) => `${bold ? 'font-bold' : 'font-medium'} ${LINK_BASE_CLASS}`;

/**
 * A `{ t: 'link' }` href that leaves the app: mailto:, tel:, or an absolute
 * http(s) URL. These render as a real `<a>` — a react-router `<Link>` treats
 * `mailto:support@oro.fit` as a relative in-app route (#2940). Keep in
 * lock-step with the native predicate in
 * `mobile/src/components/content/ContentDocView.tsx`.
 */
const EXTERNAL_HREF = /^(mailto:|tel:|https?:)/i;
const HTTP_HREF = /^https?:/i;
/**
 * Protocol-relative href (`//host/path`). Cross-origin like an absolute URL, but
 * it slips both `EXTERNAL_HREF` (no scheme) and a naive `startsWith('/')`
 * app-relative check. Treated as external, new-tab, `noopener` — never a
 * same-tab anchor or a react-router `<Link>` (which would still render
 * `<a href="//host">` and navigate cross-origin in the same tab). George r3 F8.
 */
const PROTOCOL_RELATIVE_HREF = /^\/\//;
/**
 * A `{ t: 'link' }` href that LEAVES the app entirely — an external scheme
 * (mailto:/tel:/http(s)) or a protocol-relative `//host` (cross-origin). The
 * navOverride guard and the external-anchor branch both key on this ONE helper
 * so they cannot diverge: a `//host` link must render a safe external anchor,
 * never a host-intercepted navOverride button (Frank round 4).
 */
const leavesApp = (href: string) =>
  EXTERNAL_HREF.test(href) || PROTOCOL_RELATIVE_HREF.test(href);

/**
 * #3324: when set, in-app navigations (`webRender: 'button'` inlines AND
 * plain in-app `<Link>` inlines) call this instead of routing. `null` = the
 * standalone-page default (react-router navigation). External hrefs never
 * consult it.
 */
const ContentDocNavContext = createContext<((href: string) => void) | null>(null);

/** Compile-time exhaustiveness backstop — a new variant must add a case. */
function assertNever(value: never): null {
  console.error('[ContentDocView] unhandled content variant:', value);
  return null;
}

function InlineView({ inline }: { inline: ContentInline }) {
  const navigate = useNavigate();
  const navOverride = useContext(ContentDocNavContext);

  if (typeof inline === 'string') return <>{inline}</>;

  switch (inline.t) {
    case 'strong': {
      const toneClass =
        inline.tone === 'accent'
          ? 'text-accent'
          : inline.tone === 'inherit'
            ? undefined
            : 'text-text-primary';
      return <strong className={toneClass}>{inline.v}</strong>;
    }
    case 'em':
      return <em>{inline.v}</em>;
    case 'brand':
      return <OroTrademark />;
    case 'link': {
      // #3448: `webRender: 'anchor'` forces a plain full-navigation <a> for an
      // APP-RELATIVE href. Used for `/ios`, a server 302 in _redirects with no
      // client route: a react-router <Link> would hit the SPA catch-all and
      // never reach the redirect. Checked FIRST so it wins over navOverride and
      // the in-app <Link> default alike — this link must leave via the browser,
      // not the router. Same-tab (no target), since /ios is our own redirect,
      // not a third-party site.
      //
      // Fail closed on the app-relative check (George round 2 F7): the hint is
      // only meant for our own redirect paths. An absolute URL that carried it
      // would same-tab navigate here and SKIP the external-link handling below
      // (new tab + `rel="noopener noreferrer"`), so require a leading `/` and
      // let anything else fall through to the EXTERNAL_HREF branch.
      //
      // George round 3 F8: a PROTOCOL-RELATIVE href (`//evil.example.com`) also
      // starts with `/` but is cross-origin — exclude it so it falls through to
      // external handling too. App-relative paths are a single `/` NOT followed
      // by another.
      if (
        inline.webRender === 'anchor' &&
        inline.href.startsWith('/') &&
        !inline.href.startsWith('//')
      ) {
        return (
          <a href={inline.href} className={linkClass(inline.bold)}>
            {inline.v}
          </a>
        );
      }
      // #3324: inside a dialog the host intercepts in-app navigation so the
      // surface is not torn down. Covers BOTH the `webRender: 'button'` inlines
      // and the plain in-app `<Link>` inlines — a react-router route change
      // from either would leave the login flow. External hrefs still route
      // normally (they never disturb the host surface).
      if (navOverride && !leavesApp(inline.href)) {
        const href = inline.href;
        return (
          <button type="button" onClick={() => navOverride(href)} className={linkClass(inline.bold)}>
            {inline.v}
          </button>
        );
      }
      if (inline.webRender === 'button') {
        return (
          <button
            type="button"
            onClick={() => navigate(inline.href)}
            className={linkClass(inline.bold)}
          >
            {inline.v}
          </button>
        );
      }
      if (leavesApp(inline.href)) {
        // http(s) and protocol-relative are cross-origin → new tab + noopener.
        // mailto:/tel: open the OS handler in place (no new tab).
        const newTab = HTTP_HREF.test(inline.href) || PROTOCOL_RELATIVE_HREF.test(inline.href);
        const httpProps = newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
        return (
          <a href={inline.href} className={linkClass(inline.bold)} {...httpProps}>
            {inline.v}
          </a>
        );
      }
      return (
        <Link to={inline.href} className={linkClass(inline.bold)}>
          {inline.v}
        </Link>
      );
    }
    case 'break':
      return <br />;
    case 'mono':
      return <span className="font-mono text-xs">{inline.v}</span>;
    default:
      return assertNever(inline);
  }
}

function Inlines({ inlines }: { inlines: ContentInline[] }) {
  return (
    <>
      {inlines.map((inline, i) => (
        <InlineView key={i} inline={inline} />
      ))}
    </>
  );
}

function BlockView({ block }: { block: ContentDocBlock }) {
  switch (block.type) {
    case 'p': {
      const classes = [
        block.emphasis ? 'text-text-primary font-medium' : undefined,
        block.lead ? 'text-lg' : undefined,
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <p className={classes || undefined}>
          <Inlines inlines={block.inlines} />
        </p>
      );
    }
    case 'list': {
      if (block.style === 'plain') {
        return (
          <div className="pl-4 space-y-2">
            {block.items.map((item, i) => (
              <p key={i}>
                <Inlines inlines={item} />
              </p>
            ))}
          </div>
        );
      }
      const ListTag = block.style === 'numbered' ? 'ol' : 'ul';
      const listClass =
        block.style === 'numbered'
          ? 'list-decimal list-inside space-y-1 ml-4'
          : 'list-disc list-inside space-y-2 ml-4';
      return (
        <ListTag className={listClass}>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inlines inlines={item} />
            </li>
          ))}
        </ListTag>
      );
    }
    case 'labeled':
      return (
        <div>
          <p className="font-medium text-text-primary mb-2">
            <Inlines inlines={block.label} />
          </p>
          <div className="space-y-3">
            {block.blocks.map((child, i) => (
              <BlockView key={i} block={child} />
            ))}
          </div>
        </div>
      );
    case 'box': {
      const boxClass = {
        accent: 'p-3 bg-accent/10 rounded border border-accent/20',
        surface: 'mt-4 p-3 bg-oro-surface rounded border border-oro-border',
        'surface-alt': 'p-4 bg-oro-surface-alt rounded-lg border border-oro-border',
      }[block.tone];
      return (
        <div className={boxClass}>
          {block.title && <p className="font-medium text-text-primary mb-2">{block.title}</p>}
          <div className="space-y-3">
            {block.blocks.map((child, i) => (
              <BlockView key={i} block={child} />
            ))}
          </div>
        </div>
      );
    }
    default:
      return assertNever(block);
  }
}

function SectionView({ section, doc }: { section: ContentDocSection; doc: ContentDoc }) {
  return (
    <Section
      title={section.title ? <Inlines inlines={section.title} /> : undefined}
      headingLevel={section.headingLevel}
      showDivider={section.showDivider ?? doc.sectionDividers ?? false}
      className={section.align === 'center' ? 'text-center' : undefined}
    >
      {section.blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </Section>
  );
}

interface ContentDocViewProps {
  doc: ContentDoc;
  /** Rendered after the sections (e.g. the About page's login CTA). */
  children?: ReactNode;
  /**
   * #3324: intercept in-app navigation instead of routing away. Set by the
   * login legal modal so a link inside the doc switches the doc in place
   * (terms <-> privacy) or closes and routes, rather than tearing down the
   * host surface. Omitted on the standalone pages (route normally).
   */
  onNavigate?: (href: string) => void;
}

export default function ContentDocView({ doc, children, onNavigate }: ContentDocViewProps) {
  return (
    <ContentDocNavContext.Provider value={onNavigate ?? null}>
      <PageHeader title={<Inlines inlines={doc.title} />} subtitle={doc.subtitle} />

      {doc.callout && (
        <CalloutCard title={doc.callout.title}>
          {doc.callout.blocks.map((block, i) => (
            <BlockView key={i} block={block} />
          ))}
        </CalloutCard>
      )}

      {doc.lastUpdated && doc.lastUpdatedPosition !== 'bottom' && (
        <LastUpdated date={doc.lastUpdated} />
      )}

      {doc.groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && !doc.sectionDividers && <Divider />}
          {group.length > 1 ? (
            <SectionGroup>
              {group.map((section, i) => (
                <SectionView key={i} section={section} doc={doc} />
              ))}
            </SectionGroup>
          ) : (
            group.map((section, i) => <SectionView key={i} section={section} doc={doc} />)
          )}
        </Fragment>
      ))}

      {doc.lastUpdated && doc.lastUpdatedPosition === 'bottom' && (
        <LastUpdated date={doc.lastUpdated} />
      )}

      {children}
    </ContentDocNavContext.Provider>
  );
}
